import React, { useState } from "react";
import {Link, Navigate, useLocation} from "react-router-dom";
import {
	Alert,
	Button,
	FormGroup,
	Form,
	Container,
	Row,
	Col,
} from "react-bootstrap";
import { Formik } from "formik";
import { useAuth } from "../../AuthUser";
import API from "../../api";
import {Helmet} from "react-helmet";

function useQuery() {
	const { search } = useLocation();

	return React.useMemo(() => new URLSearchParams(search), [search]);
}
const ResetPassword = () => {
	const { token } = useAuth();
	let query = useQuery();
	const [done, setDone] = useState(false);
	const [submitError, setSubmitError] = useState(null);

	if (token) {
		return <Navigate to="/account" replace />;
	}

	if (!query.get('token')) {
		return <Navigate to="/" replace />;
	}

	// Success state replaces the form: no auto-redirect to /signin — a toast
	// there vanished before users could read it (MGDWD-159 feedback).
	if (done) {
		return (
			<>
				<Helmet>
					<title>Reset Password | MyGolfDNA.com</title>
					<meta name="description" content="reset your password"/>
				</Helmet>
				<Container>
					<Row>
						<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
							<div className="text-center mt-5">
								<h2 className="text-white">Password updated</h2>
								<p className="text-white-50 mt-3">
									Your password has been changed. Use it the next time you sign in.
								</p>
								<div className="d-grid mt-4">
									<Button variant="secondary" size="lg" as={Link} to="/signin">
										Go to Sign in
									</Button>
								</div>
							</div>
						</Col>
					</Row>
				</Container>
			</>
		);
	}

	return (
		<Formik
			initialValues={{ confirmPassword: "", password: "", token: null}}
			validate={(values) => {
				const errors = {};
				if (!values.password) {
					errors.password = "Required";
				}
				if (!values.confirmPassword) {
					errors.confirmPassword = "Passwords must match";
				}
				if (values.password && values.confirmPassword) {
					if (values.password !== values.confirmPassword) {
						errors.confirmPassword = "Passwords must match";
						errors.password = "Passwords must match";
					}
				}

				return errors;
			}}
			onSubmit={(values, { setSubmitting, setErrors }) => {
				setSubmitError(null);
				setTimeout(() => {
					API.post('auth/reset-password', {
						token: query.get('token'),
						password: values.password,
						confirmPassword: values.confirmPassword,
					})
						.then(() => {
							setDone(true);
							setSubmitting(false);
						})
						.catch((error) => {
							setSubmitting(false);
							// Persistent on-page error: used/expired links need a
							// message the user can actually read.
							setSubmitError(error.response?.data?.message || "Something went wrong. Please try again.");
							if (
								error.response &&
								error.response.data.errors &&
								error.response.data.errors.length
							) {
								const result = error.response.data.errors.reduce(function (
										map,
										obj
									) {
										map[obj.property] = obj.message;
										return map;
									},
									{});
								setErrors(result);
							}
						});
				}, 400);
			}}
		>
			{({
					values,
					errors,
					touched,
					handleChange,
					handleSubmit,
					isSubmitting,
				}) => (
				<>
					<Helmet>
						<title>Reset Password | MyGolfDNA.com</title>
						<meta name="description" content="reset your password"/>
					</Helmet>
					<Container>
						<Row>
							<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
								<h2 className="text-white text-center mt-5">Reset Password</h2>
								{submitError && (
									<Alert variant="danger" className="mt-4 mb-0">
										{submitError}{" "}
										<Alert.Link as={Link} to="/forgot-password">Request a new link</Alert.Link>
									</Alert>
								)}
								<Form noValidate className="mt-5 form-group" onSubmit={handleSubmit}>
									<Form.Group className="form-floating mb-3">
										<Form.Control
											className="bg-dark border-light border-opacity-25 text-light"
											placeholder="new password"
											type="password"
											autoComplete="new-password"
											name="password"
											id="password"
											onChange={handleChange}
											value={values.password}
											isInvalid={errors.password && touched.password}
											isValid={!errors.password && touched.password}
										/>
										<Form.Label className="text-white" htmlFor="password">New Password</Form.Label>
										<div className="invalid-feedback">{errors.password}</div>
									</Form.Group>

									<Form.Group className="form-floating mb-3">
										<Form.Control
											className="bg-dark border-light border-opacity-25 text-light"
											placeholder="confirm new password"
											type="password"
											autoComplete="new-password"
											name="confirmPassword"
											id="confirmPassword"
											onChange={handleChange}
											value={values.confirmPassword}
											isInvalid={errors.confirmPassword && touched.confirmPassword}
											isValid={!errors.confirmPassword && touched.confirmPassword}
										/>
										<Form.Label className="text-white" htmlFor="confirmPassword">Confirm New Password</Form.Label>
										<div className="invalid-feedback">{errors.confirmPassword}</div>
									</Form.Group>
									<FormGroup className="mt-5">
										<div className="d-grid gap-4">
											<Button variant="secondary" size="lg" type="submit" disabled={isSubmitting}>
												{isSubmitting ? (
													<>
													<span
														className="spinner-border spinner-border-sm"
														role="status"
														aria-hidden={true}
													/>{" "}
													</>
												) : (
													"Update Password"
												)}
											</Button>
										</div>
									</FormGroup>
								</Form>
							</Col>
						</Row>
					</Container>
				</>
			)}
		</Formik>
	);
};

export default ResetPassword;
